import { useEffect, useRef } from 'react'
import { useLocalObservable } from 'mobx-react-lite'

import type { Disposer } from '@/utils/mobx/combineDisposers'

// Optional hooks the creator may call on the store; a store implements only what it
// needs. subscribe must return a disposer (runs on unmount).
type ManageableStore = {
  setProps?: (props: never) => void
  subscribe?: () => Disposer
}

export type StoreConstructor = new (props: never) => object

// Props type inferred from the constructor's first argument; a no-arg constructor means
// no props.
export type StoreProps<C extends StoreConstructor> =
  ConstructorParameters<C> extends [infer P, ...unknown[]] ? P : Record<never, never>

// Props are required unless the store has none.
type CreateStoreArgs<Props> = keyof Props extends never
  ? [props?: Props, options?: { subscribe?: boolean }]
  : [props: Props, options?: { subscribe?: boolean }]

/**
 * Low-level factory: returns a hook that creates and manages a store instance (create
 * once, sync props, subscribe) — no context/provider. Use it when a single component owns
 * the store and threads it down explicitly. `getStoreProvider` is built on top of this.
 */
export const getStoreCreator = <C extends StoreConstructor>(StoreClass: C) => {
  type T = InstanceType<C>
  type Props = StoreProps<C>

  const useCreateStore = (...args: CreateStoreArgs<Props>): T => {
    const [props = {} as Props, options] = args
    const subscribe = options?.subscribe ?? true

    const store = useLocalObservable<T>(() => new StoreClass(props as never) as T)

    // The constructor already set the initial props, so skip the mount run to avoid a
    // duplicate setProps.
    const isMounted = useRef(false)
    useEffect(() => {
      if (!isMounted.current) {
        isMounted.current = true
        return
      }
      ;(store as ManageableStore).setProps?.(props as never)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [store, ...Object.values(props as Record<string, unknown>)])

    // Re-runs when the flag flips (off → dispose, on → re-subscribe).
    useEffect(() => {
      if (!subscribe) return
      return (store as ManageableStore).subscribe?.()
    }, [store, subscribe])

    return store
  }

  return useCreateStore
}
